import { KeyboardInput } from './input/KeyboardInput';
import { TouchInput } from './input/TouchInput';
import { MouseInput } from './input/MouseInput';
import { GamepadInput } from './input/GamepadInput';

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
}

interface SoundManagerLike {
    unlockAudio(): void;
    playBGM(): void;
}

type ButtonName = 'confirm' | 'cancel' | 'up' | 'down' | 'left' | 'right';

interface DeltaResult {
    dx: number;
    dy: number;
    active: boolean;
}

/**
 * 입력 관리 통합 클래스
 * 키보드, 터치, 마우스, 게임패드 입력을 통합 관리
 */
export class InputManager {
    private keyboard: KeyboardInput;
    private touch: TouchInput;
    private mouse: MouseInput;
    private gamepad: GamepadInput;
    private renderer: RendererLike | null;

    constructor(soundManager: SoundManagerLike | null = null) {
        this.keyboard = new KeyboardInput();
        this.touch = new TouchInput(soundManager);
        this.mouse = new MouseInput();
        this.gamepad = new GamepadInput(soundManager);

        this.renderer = null;
    }

    setRenderer(renderer: RendererLike): void {
        this.renderer = renderer;
        this.touch.setRenderer(renderer);
        this.mouse.setRenderer(renderer);
    }

    update(): void {
        this.keyboard.update();
        this.touch.update();
        this.gamepad.update();
    }

    getAxis(axisName: 'horizontal' | 'vertical'): number {
        // 키보드 입력
        let value = this.keyboard.getAxis(axisName);

        // 게임패드 입력 (키보드가 0일 때만)
        if (value === 0) {
            value = this.gamepad.getAxis(axisName);
        }

        // 터치/마우스 드래그는 Player.update()에서 직접 처리
        if (this.touch.isActive() || this.mouse.isActive()) {
            return 0;
        }

        return Math.max(-1, Math.min(1, value));
    }

    isButtonPressed(buttonName: ButtonName): boolean {
        if (buttonName === 'confirm') {
            return this.keyboard.isConfirmPressed() ||
                   this.gamepad.isConfirmPressed() ||
                   this.touch.isConfirmJustPressed();
        } else if (buttonName === 'cancel') {
            return this.keyboard.isCancelPressed() ||
                   this.gamepad.isCancelPressed();
        } else if (buttonName === 'up') {
            return this.keyboard.isUpPressed() ||
                   this.gamepad.isUpPressed();
        } else if (buttonName === 'down') {
            return this.keyboard.isDownPressed() ||
                   this.gamepad.isDownPressed();
        } else if (buttonName === 'left') {
            return this.keyboard.isLeftPressed() ||
                   this.gamepad.isLeftPressed();
        } else if (buttonName === 'right') {
            return this.keyboard.isRightPressed() ||
                   this.gamepad.isRightPressed();
        }
        return false;
    }

    isAnyButtonPressed(): boolean {
        return this.isButtonPressed('confirm') || this.isButtonPressed('cancel');
    }

    getTouchDelta(): DeltaResult {
        // 터치 우선
        const touchDelta = this.touch.getDelta();
        if (touchDelta.active) {
            return touchDelta;
        }

        // 마우스 드래그
        return this.mouse.getDelta();
    }

    resetTouchDelta(): void {
        this.touch.resetDelta();
        this.mouse.resetDelta();
    }

    cancelTouchConfirm(): void {
        this.touch.cancelConfirm();
    }

    // 레거시 호환성
    isKeyDown(...keys: string[]): boolean {
        return this.keyboard.isKeyDown(...keys);
    }

    destroy(): void {
        this.keyboard.destroy();
        this.touch.destroy();
        this.mouse.destroy();
        this.gamepad.destroy();
        this.renderer = null;
    }
}
